export const MAX_GAME_CARDS = 6;

export const getGames = (
  games: Array<{ id: string }>,
  excludeGameIds: Array<string>
) => {
  if (!games?.length || !Array.isArray(games)) {
    return [];
  }

  if (excludeGameIds.length === 0) {
    return games;
  }

  if (games.length > MAX_GAME_CARDS) {
    return games.filter((item) => !excludeGameIds.includes(item.id));
  }

  return games;
};

type Matrix<T> = Array<Array<T>>;

/**
 * Please, check tests for deep understanding
 * general concept - we have two ways
 * 1. When we have more items, than available spaces - we will play them in order
 * 2. When we have less or equal items - we will put it from left to right
 */
export const convertGamesListToMatrix = <T>(
  items: Array<T> = [],
  desirableLines: number = 1,
  itemsInRow: number = 1,
  isColOrder: boolean = false,
  isScrollable: boolean = true
): Matrix<T> => {
  const availablePlaces = itemsInRow * desirableLines;
  const collectionLength = items.length;

  const itemsLargerThanPlaces = collectionLength >= availablePlaces;
  const actualLines = Math.ceil(collectionLength / itemsInRow);

  if (desirableLines <= 1) {
    return [isScrollable ? items : items.slice(0, itemsInRow)];
  }

  if (!itemsLargerThanPlaces) {
    return isColOrder
      ? convertToMatrixByCol<T>(items, itemsInRow, actualLines)
      : convertToMatrixByRows<T>(
          items,
          Math.max(itemsInRow, Math.ceil(collectionLength / actualLines))
        );
  }

  const itemsAfterScrollCheck = isScrollable
    ? items
    : items.slice(0, availablePlaces);

  return isColOrder
    ? convertToMatrixByColWithRedundant<T>(
        itemsAfterScrollCheck,
        desirableLines
      )
    : convertToMatrixByRows<T>(
        itemsAfterScrollCheck,
        Math.ceil(itemsAfterScrollCheck.length / desirableLines)
      );
};

export const chunkify = <T>(
  arr: Array<T> = [],
  chunkSize: number = 1
): Matrix<T> => {
  const array = [...arr];
  const result = [];

  for (let i = 0; i < array.length; i += chunkSize) {
    result.push(array.slice(i, i + chunkSize));
  }

  return result;
};

const convertToMatrixByCol = <T>(
  items: Array<T> = [],
  itemsInRow: number = 1,
  rows: number = 1
): Matrix<T> => {
  const result = Array.from({ length: rows }, () => []);

  const fullMatrixItemsLength = itemsInRow * rows;
  const lastRowColLimit =
    itemsInRow - (fullMatrixItemsLength - items.length) - 1;
  const lastRowIndex = rows - 1;

  let i = 0;
  for (let col = 0; col < itemsInRow; col++) {
    for (let row = 0; row < rows; row++) {
      if (col > lastRowColLimit && row === lastRowIndex) {
        continue;
      }
      result[row][col] = items[i++];

      // TODO: maybe not needed
      if (i > items.length) {
        break;
      }
    }
  }

  return result.map((row) => row.filter(Boolean));
};

const convertToMatrixByRows = <T>(
  items: Array<T> = [],
  itemsInRow: number = 1
): Matrix<T> => chunkify(items, itemsInRow);

const convertToMatrixByColWithRedundant = <T>(
  arr: Array<T> = [],
  rows: number = 1
): Matrix<T> => {
  const result = Array.from({ length: rows }, () => []);
  arr.forEach((item, index) => {
    result[index % rows].push(item);
  });
  return result;
};
